import React from 'react';
import { isMobile } from 'react-device-detect';
import Dropzone from 'react-dropzone';
import Webcam from 'react-webcam';

import Backdrop from '@material-ui/core/Backdrop';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import AddPhotoAlternateIcon from '@material-ui/icons/AddPhotoAlternate';
import ClearRoundedIcon from '@material-ui/icons/ClearRounded';
import DoneRoundedIcon from '@material-ui/icons/DoneRounded';
import Alert from '@material-ui/lab/Alert';
import classnames from 'classnames';
import { BLUR_THRESHOLD, VIDEO_INPUT, WebcamWidths } from 'constants/camera';
import { MAX_ATTACHMENT_FILE_SIZE_IN_BYTES } from 'constants/environment';
import { KEY_DOWN_EVENT } from 'constants/events';
import {
  filter,
  first,
  forEach,
  get,
  isEmpty,
  isNull,
  map,
  some,
  zipObject
} from 'lodash';

import TaskInfo from 'pages/Students/Assignments/Detail/components/TaskInfo/index';
import ImageCropper from 'pages/Teachers/Insights/components/ImageCropper';
import { compressImage, fileToBase64 } from 'utils/files';
import {
  blobToImage,
  centerCrop,
  detectBlur,
  detectEdges,
  getCroppedImgBlob
} from 'utils/files/images';
import { notifyError } from 'utils/notifications';

import Timer from 'components/Timer';
import WebcamError from 'components/WebcamError';

import styles from './styles.module.scss';

const white = withStyles({
  root: {
    color: 'var(--white)',
    fontSize: '3rem'
  }
});
const StyledDoneRoundedIcon = white(DoneRoundedIcon);
const StyledClearRoundedIcon = white(ClearRoundedIcon);

const StyledLoading = withStyles({
  root: {
    color: 'var(--white)'
  }
})(CircularProgress);

class WebcamCapture extends React.Component {
  constructor(props) {
    super(props);

    this.webcamRef = React.createRef();
    this.canvasRef = React.createRef();

    this.lastShot = null;
    this.lastBlurScore = 0;
    this.animationFrameId = null;
  }

  state = {
    cameraError: null,
    qualityError: null,
    capturedImagesData: this.initializeImages(),
    crop: centerCrop({
      widthPercent: 65
    }),
    croppedImageBlob: null,
    croppedImageUrl: null,
    croppingActive: false,
    sourceImage: null,
    startTimer: false,
    findBestFrame: false,
    previewImage: null,
    uploadedImageBlob: null, // storing the base64 of the image before the upload confirmation
    documentScanActive: false,
    documentScanCancelled: false
  };

  componentWillUnmount() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }

    window.removeEventListener(KEY_DOWN_EVENT, this.handleKeyDown);
  }

  async componentDidMount() {
    window.addEventListener(KEY_DOWN_EVENT, this.handleKeyDown);
  }

  handleKeyDown = (e) => {
    if (e.code === 'Space') {
      e.preventDefault();
      const scanningFlow = isMobile
        ? this.capturePhotoMobile
        : this.startCapture;

      scanningFlow();
    }
  };

  initializeImages() {
    // Return value: { [task.id]: null }
    const { tasks } = this.props;

    return zipObject(map(tasks, 'id'), {
      image: null,
      submitting: false
    });
  }

  // Passes lower quality image source into detectBlur. If the current frame is
  // less blurry than previous frames, the program stores the source data for
  // the higher-quality image. Using the lower quality data for analysis alows us
  // to compute the blur in real time since it doesn't take as much computation.
  tick = () => {
    if (!this.webcamRef.current) {
      return;
    }

    // The timer can be reset. For example when the task has changed during the capture phase
    if (!this.state.startTimer) {
      this.resetBlurImageHelpers();
      return;
    }

    const imgBase64 = this.webcamRef.current.getScreenshot();

    const smallImage = document.createElement('img');

    smallImage.onload = () => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = 300; // downsize the image so the width is 300 px wide
      canvas.height = (300 * smallImage.height) / smallImage.width;

      context.drawImage(smallImage, 0, 0, canvas.width, canvas.height);

      const blurScore = detectBlur(
        detectEdges(
          context.getImageData(0, 0, canvas.width, canvas.height),
          context,
          canvas
        ),
        context,
        canvas
      );

      if (blurScore > this.lastBlurScore) {
        // Store the least blurry frame and its blur score in global variables
        this.lastBlurScore = blurScore;
        this.lastShot = imgBase64;
      }
    };

    smallImage.onerror = notifyError;

    smallImage.src = imgBase64;

    // analyze a new frame every 0.1 seconds
    setTimeout(() => {
      this.animationFrameId = requestAnimationFrame(this.tick);
    }, 100);
  };

  onUserMediaError = (error) => {
    this.setState({ cameraError: error });
  };

  changeTask = ({ step }) => {
    this.props.changeTask({ step }).then((activeTask) => {
      const previewImage = get(
        this.state.capturedImagesData,
        `${activeTask.id}.image`
      );

      this.setState(
        {
          previewImage,
          findBestFrame: false,
          startTimer: false,
          croppingActive: false
        },
        this.resetBlurImageHelpers
      );
    });
  };

  // This function is called when the student starts the timer.
  // It waits until the 1-second mark to start the blur detection.
  startCapture = () => {
    if (this.animationFrameId !== null) {
      return;
    }
    window.navigator.mediaDevices.enumerateDevices().then((devices) => {
      const videoDevices = filter(devices, { kind: VIDEO_INPUT });

      if (isEmpty(videoDevices) || !some(videoDevices, 'label')) {
        // Camera device is not allowed
        this.setState({ cameraError: { name: 'TrackStartError' } });
        return;
      }
    });

    this.setState({ startTimer: true });

    // Wait two seconds before looking for the best frame
    setTimeout(() => {
      this.setState({ findBestFrame: true }, () => {
        // Check https://css-tricks.com/using-requestanimationframe/ for more info.
        this.animationFrameId = requestAnimationFrame(this.tick);
      });
    }, 200);
  };

  // This function is called when the user is capturing photos from mobile devices
  capturePhotoMobile = () => {
    if (!this.webcamRef.current) {
      return;
    }

    const imgBase64 = this.webcamRef.current.getScreenshot();
    if (isNull(imgBase64)) {
      this.setState({ cameraError: { name: 'TrackStartError' } });
      return;
    }

    const finalImage = document.createElement('img');

    // info for cropping images
    const topNavBarPosit = 48; // top nav bar has height of 48 px
    const webcamBoundingRect = this.webcamDiv
      ? this.webcamDiv.getBoundingClientRect()
      : null;
    const taskNavBoundingRect = this.taskNav
      ? this.taskNav.getBoundingClientRect()
      : null;

    let overflowingBottom, overflowingTop, capButtonTop;
    if (webcamBoundingRect && taskNavBoundingRect) {
      capButtonTop = taskNavBoundingRect.top + 2; // add 2 pixels to account for border
      overflowingBottom = webcamBoundingRect.bottom > capButtonTop; // indicates whether image overflows buttons
      overflowingTop = webcamBoundingRect.top < topNavBarPosit; // indicates whether image overflows top nav bar
    }

    finalImage.onload = () => {
      let capturedImage = imgBase64;

      if (overflowingBottom || overflowingTop) {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        const croppedHeight =
          (finalImage.height * (capButtonTop - topNavBarPosit)) /
          webcamBoundingRect.height;

        const startY = overflowingTop
          ? (finalImage.height * (topNavBarPosit - webcamBoundingRect.top)) /
            webcamBoundingRect.height
          : 0;

        canvas.width = finalImage.width;
        canvas.height = croppedHeight;
        context.drawImage(
          finalImage,
          0,
          startY,
          finalImage.width,
          croppedHeight,
          0,
          0,
          finalImage.width,
          croppedHeight
        );

        capturedImage = canvas.toDataURL();
      }

      // if (this.lastBlurScore < MOBILE_BLUR_THRESHOLD) {
      //   notifyError(
      //     'The image is too blurry. Please try again with a clearer image.'
      //   );
      //   this.setState(
      //     {
      //       capturedImagesData: {
      //         ...this.state.capturedImagesData
      //       },
      //       previewImage: capturedImage,
      //       qualityError: {
      //         name: 'BlurError',
      //         message: `The image is too blurry.`
      //       }
      //     },
      //     this.resetBlurImageHelpers
      //   );
      // } else {
      this.setState({
        capturedImagesData: {
          ...this.state.capturedImagesData,
          [this.props.activeTask.id]: {
            ...this.state.capturedImagesData[this.props.activeTask.id],
            image: capturedImage
          }
        },
        previewImage: capturedImage,
        croppingActive: true
      });
    };
    finalImage.onerror = notifyError;
    finalImage.src = imgBase64;
  };

  resetBlurImageHelpers = () => {
    this.lastShot = null;
    this.lastBlurScore = 0;
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    this.animationFrameId = null;
  };

  // This function is called when the user is capturing photos from a laptop. It is called
  // once the timer hits zero. It checks whether the final image must be mirrored and acts
  // accordingly. Then it saves the final image by calling setState.
  capturePhotoLaptop = () => {
    const finalImage = document.createElement('img');

    finalImage.onload = () => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      canvas.width = finalImage.width;
      canvas.height = finalImage.height;

      context.translate(canvas.width, 0);
      context.scale(-1, 1);
      context.drawImage(
        finalImage,
        0,
        0,
        finalImage.width,
        finalImage.height,
        0,
        0,
        finalImage.width,
        finalImage.height
      );

      if (this.lastBlurScore < BLUR_THRESHOLD) {
        notifyError(
          'The image is too blurry. Please try again with a clearer image.'
        );
        this.setState(
          {
            capturedImagesData: {
              ...this.state.capturedImagesData
            },
            previewImage: canvas.toDataURL(),
            qualityError: {
              name: 'BlurError',
              message: `The image is too blurry.`
            }
          },
          this.resetBlurImageHelpers
        );
      } else {
        this.setState(
          {
            capturedImagesData: {
              ...this.state.capturedImagesData,
              [this.props.activeTask.id]: {
                ...this.state.capturedImagesData[this.props.activeTask.id],
                image: canvas.toDataURL()
              }
            },
            previewImage: canvas.toDataURL(),
            croppingActive: true,
            qualityError: null
          },
          this.resetBlurImageHelpers
        );
      }
    };

    finalImage.onerror = notifyError;

    // this line triggers the finalImage.onload function
    finalImage.src = this.lastShot;
  };

  removeCapturedPhoto = () => {
    this.setState(
      {
        capturedImagesData: {
          ...this.state.capturedImagesData,
          [this.props.activeTask.id]: {
            ...this.state.capturedImagesData[this.props.activeTask.id],
            image: null
          }
        },
        findBestFrame: false,
        startTimer: false,
        previewImage: null,
        croppingActive: false
      },
      this.resetBlurImageHelpers
    );
  };

  uploadCapturedPhoto = async () => {
    const { activeTask, submitting, uploadStudentWork } = this.props;
    const { crop, croppedImageBlob } = this.state;

    const activeTaskImg = get(
      this.state.capturedImagesData,
      `${activeTask.id}.image`
    );

    if (!activeTaskImg || !crop) return;

    // Set submitting state
    this.setState({
      capturedImagesData: {
        ...this.state.capturedImagesData,
        [activeTask.id]: {
          image: activeTaskImg,
          submitting: true
        }
      },
      findBestFrame: false,
      startTimer: false,
      previewImage: activeTaskImg,
      croppingActive: true
    });

    await uploadStudentWork(
      croppedImageBlob ? croppedImageBlob : activeTaskImg
    );

    // Stop submitting state
    this.setState(
      {
        capturedImagesData: {
          ...this.state.capturedImagesData,
          [activeTask.id]: {
            image: null,
            submitting: false
          }
        },
        findBestFrame: false,
        startTimer: false,
        previewImage: null,
        croppingActive: false
      },
      this.resetBlurImageHelpers
    );

    if (!submitting) {
      this.changeTask({ step: 1 });
    }
  };

  getBlurScore = (image) => {
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(image, 0, 0, image.width, image.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    return detectBlur(detectEdges(imageData, ctx, canvas), ctx, canvas);
  };

  saveImageBlob = (files) => {
    compressImage(files[0], {
      maxSizeMB: 1,
      maxWidth: 1280,
      maxHeight: 720
    })
      .then((compressedBlob) =>
        blobToImage(compressedBlob).then((img) => {
          const blurScore = this.getBlurScore(img);

          if (blurScore < BLUR_THRESHOLD) {
            throw new Error('Image is too blurry, please retake.');
          }

          return fileToBase64(compressedBlob);
        })
      )
      .then((base64) => {
        this.setState({
          uploadedImageBlob: base64,
          previewImage: base64,
          croppingActive: true
        });
      })
      .catch(notifyError);
  };

  uploadSavedImageBlob = async () => {
    if (this.state.uploadedImageBlob) {
      const { activeTask } = this.props;

      // Set submitting state
      this.setState({
        capturedImagesData: {
          ...this.state.capturedImagesData,
          [activeTask.id]: {
            submitting: true
          }
        },
        croppingActive: false
      });

      await this.props.uploadStudentWork(this.state.uploadedImageBlob);

      // Stop submitting state
      this.setState({
        capturedImagesData: {
          ...this.state.capturedImagesData,
          [activeTask.id]: {
            submitting: false
          }
        },
        uploadedImageBlob: null,
        previewImage: null,
        croppingActive: false
      });
    }
  };

  removeSavedImageBlob = () => {
    if (this.state.uploadedImageBlob) {
      this.setState({
        uploadedImageBlob: null,
        previewImage: null,
        croppingActive: false
      });
    }
  };

  onDropRejected = (files) =>
    forEach(files, (file) =>
      notifyError(`${file.name} exceeds the size limit of 20 MB.`)
    );

  getTaskImagePreview = (task) => {
    const { capturedImagesData } = this.state;
    const imageData = get(capturedImagesData, task.id);
    const currentImgSrc = get(imageData, 'image');

    if (currentImgSrc) {
      return currentImgSrc;
    }

    return get(first(task.work), 'work_url');
  };

  handleCrop = async () => {
    const { sourceImage, crop } = this.state;

    if (!sourceImage || !crop?.width || !crop?.height) return;

    const blob = await getCroppedImgBlob({ image: sourceImage, crop });
    const url = URL.createObjectURL(blob);

    this.setState({
      croppedImageBlob: blob,
      croppedImageUrl: url,
      croppingActive: false
    });
  };

  render() {
    const {
      activeTaskName,
      cameraError,
      capturedImagesData,
      crop,
      croppedImageUrl,
      croppingActive,
      documentScanActive,
      documentScanCancelled,
      startTimer,
      previewImage,
      qualityError,
      uploadedImageBlob
    } = this.state;
    const { activeTask } = this.props;

    const imageData = get(capturedImagesData, activeTask.id);

    const currentImgSubmitting = get(imageData, 'submitting');

    const qualityErrorMessage = qualityError ? qualityError.message : null;

    return (
      <div
        className={previewImage ? styles.webcamViewSetDims : styles.webcamView}
      >
        {!cameraError && !previewImage && (
          <div ref={(el) => (this.webcamDiv = el)}>
            <Webcam
              className={styles.webcam}
              audio={false}
              ref={this.webcamRef}
              forceScreenshotSourceSize={true}
              screenshotFormat="image/jpeg"
              mirrored={!isMobile}
              screenshotQuality={1}
              videoConstraints={{
                width: isMobile
                  ? WebcamWidths.WEBCAM_MOBILE
                  : WebcamWidths.WEBCAM_DESKTOP,
                deviceId: this.props.selectedCameraId
                  ? { exact: this.props.selectedCameraId }
                  : undefined,
                facingMode: !this.props.selectedCameraId && 'environment'
              }}
              onUserMediaError={this.onUserMediaError}
            />
          </div>
        )}

        {currentImgSubmitting && (
          <Backdrop open style={{ zIndex: 1 }}>
            <Box textAlign="center">
              <StyledLoading size={52} />
              <Box color="white" fontSize="1.4rem" marginTop={2}>
                Uploading...
              </Box>
            </Box>
          </Backdrop>
        )}

        {(previewImage || uploadedImageBlob) && (
          <ImageCropper
            activeTaskName={activeTaskName}
            crop={crop}
            croppedImageUrl={croppedImageUrl}
            croppingActive={croppingActive}
            previewImage={previewImage}
            setCrop={(c) => this.setState({ crop: c })}
            setCroppedBlob={(imageBlob) =>
              this.setState({ croppedImageBlob: imageBlob })
            }
            setCroppedImageUrl={(imageUrl) =>
              this.setState({ croppedImageUrl: imageUrl })
            }
            setSourceImage={(sourceImage) => this.setState({ sourceImage })}
            uploadedImageBlob={uploadedImageBlob}
          />
        )}

        {cameraError &&
          (cameraError.name === 'NotReadableError' ||
          cameraError.name === 'TrackStartError' ? (
            <div className={styles.errorContainer}>
              <WebcamError />
            </div>
          ) : (
            <Alert severity="warning">
              Cannot start camera: {cameraError.toString()}
            </Alert>
          ))}

        {!cameraError && (
          <div
            className={styles.taskNavigation}
            ref={(el) => (this.taskNav = el)}
          >
            <div className={styles.buttonsWrapper}>
              {!previewImage && !documentScanCancelled && (
                <Dropzone
                  onDrop={this.saveImageBlob}
                  maxSize={MAX_ATTACHMENT_FILE_SIZE_IN_BYTES}
                  accept={['image/jpeg', 'image/png']}
                  onDropRejected={this.onDropRejected}
                >
                  {({ getRootProps, getInputProps }) => (
                    <div className={styles.uploadButton} {...getRootProps()}>
                      <input {...getInputProps()} />
                      <IconButton>
                        <AddPhotoAlternateIcon
                          color="primary"
                          fontSize="large"
                        />
                      </IconButton>
                    </div>
                  )}
                </Dropzone>
              )}
              {!previewImage && !documentScanActive && (
                <div
                  className={classnames(styles.cameraBtn, styles.screenshot)}
                  onClick={
                    isMobile ? this.capturePhotoMobile : this.startCapture
                  }
                >
                  {startTimer && !isMobile && (
                    <Timer
                      seconds={1}
                      showDigits={false}
                      onFinish={this.capturePhotoLaptop}
                    />
                  )}
                </div>
              )}
              {/* TODO: Add a button with which the user resets the cropping */}
              {previewImage && croppingActive && (
                <div className={styles.confirmDeleteButtons}>
                  {
                    <div
                      className={classnames(styles.cameraBtn, styles.confirm)}
                      onClick={this.handleCrop}
                    >
                      <StyledDoneRoundedIcon />
                    </div>
                  }
                </div>
              )}
              {previewImage && !uploadedImageBlob && !croppingActive && (
                <div className={styles.confirmDeleteButtons}>
                  <div
                    className={classnames(styles.cameraBtn, styles.delete)}
                    onClick={this.removeCapturedPhoto}
                  >
                    <StyledClearRoundedIcon />
                  </div>
                  {qualityErrorMessage === null && (
                    <div
                      className={classnames(styles.cameraBtn, styles.confirm)}
                      onClick={this.uploadCapturedPhoto}
                    >
                      <StyledDoneRoundedIcon />
                    </div>
                  )}
                </div>
              )}

              {previewImage && uploadedImageBlob && !croppingActive && (
                <div className={styles.confirmDeleteButtons}>
                  <div
                    className={classnames(styles.cameraBtn, styles.delete)}
                    onClick={this.removeSavedImageBlob}
                  >
                    <StyledClearRoundedIcon />
                  </div>
                  <div
                    className={classnames(styles.cameraBtn, styles.confirm)}
                    onClick={this.uploadSavedImageBlob}
                  >
                    <StyledDoneRoundedIcon />
                  </div>
                </div>
              )}
              {!previewImage && !documentScanActive && (
                <div className={styles.rightSidePlaceholder} />
              )}
            </div>
            {!documentScanCancelled && (
              <TaskInfo
                task={activeTask}
                img={this.getTaskImagePreview(activeTask)}
              />
            )}
          </div>
        )}
      </div>
    );
  }
}

export default WebcamCapture;
